import React, { useState, useEffect } from "react";
import {
  Container,
  Box,
  Typography,
  Button,
  TextField,
  Paper,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Snackbar,
  Fade,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  useMediaQuery,
  useTheme,
  Tab,
  Tabs,
  InputAdornment,
} from "@mui/material";
import {
  Business as BusinessIcon,
  AttachMoney as MoneyIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Delete as DeleteIcon,
  Payment as PaymentIcon,
  Security as SecurityIcon,
  VerifiedUser as VerifiedUserIcon,
  ReceiptLong as ReceiptLongIcon,
  Search as SearchIcon,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import paymentService from "../services/paymentService";

// Colores corporativos
const colors = {
  primary: "#00E400",
  primaryHover: "#00CC00",
  secondary: "#000000",
  background: "#F5F5F5",
  white: "#FFFFFF",
  textDark: "#000000",
  textLight: "#666666",
  success: "#00E400",
  warning: "#FFC107",
  error: "#DC3545",
};

// Función para obtener el estado visual (transforma CANCELADO a PENDIENTE)
const getVisualState = (estado) => {
  const estadoLower = estado?.toLowerCase();
  // Si el estado es cancelado, lo mostramos como pendiente
  if (
    estadoLower === "cancelado" ||
    estadoLower === "cancelled" ||
    estadoLower === "canceled"
  ) {
    return "pendiente";
  }
  return estadoLower;
};

// Componente para el tab de Buscar Facturas
const BuscarFacturasTab = ({
  colors,
  onPagar,
  onEliminar,
  processingPaymentId,
  loading,
}) => {
  const [nitBusqueda, setNitBusqueda] = useState("");
  const [codigoEmpresarial, setCodigoEmpresarial] = useState("");
  const [showCodigo, setShowCodigo] = useState(false);
  const [facturasEncontradas, setFacturasEncontradas] = useState([]);
  const [buscando, setBuscando] = useState(false);
  const [errorBusqueda, setErrorBusqueda] = useState("");
  const [disabledPayments, setDisabledPayments] = useState({});

  const handleBuscarPorNit = async () => {
    if (!nitBusqueda || nitBusqueda.trim().length < 3) {
      setErrorBusqueda("El NIT debe tener al menos 3 caracteres");
      return;
    }

    if (!codigoEmpresarial || codigoEmpresarial.trim().length < 3) {
      setErrorBusqueda(
        "El código empresarial debe tener al menos 3 caracteres",
      );
      return;
    }

    setBuscando(true);
    setErrorBusqueda("");
    setFacturasEncontradas([]);

    try {
      const response = await paymentService.searchInvoicesByNitAndCode(
        nitBusqueda,
        codigoEmpresarial,
      );

      if (response.data && response.data.length > 0) {
        // Inicializar el estado de deshabilitado para cada factura
        const initialDisabledState = {};
        response.data.forEach((factura) => {
          initialDisabledState[factura.id] = false;
        });
        setDisabledPayments(initialDisabledState);
        setFacturasEncontradas(response.data);
      } else {
        setErrorBusqueda(
          "No se encontraron facturas para este NIT y código empresarial",
        );
        setFacturasEncontradas([]);
      }
    } catch (error) {
      console.error("Error al buscar facturas:", error);
      setErrorBusqueda(error.message || "Error al buscar facturas");
      setFacturasEncontradas([]);
    } finally {
      setBuscando(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleBuscarPorNit();
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const date = new Date(dateString);
    return date.toLocaleString("es-CO", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Modificada: Ahora usa getVisualState para determinar los estilos del chip
  const getChipStyles = (estado) => {
    const visualState = getVisualState(estado);

    switch (visualState) {
      case "pagado":
      case "paid":
      case "approved":
        return {
          bgcolor: "#d4edda",
          color: "#155724",
          icon: <CheckCircleIcon />,
          label: "Pagado",
        };
      case "pendiente":
      default:
        return {
          bgcolor: "#fff3cd",
          color: "#856404",
          icon: <ReceiptIcon />,
          label: "Pendiente",
        };
    }
  };

  // Verifica si el botón de pago debe estar deshabilitado
  // Ahora las facturas canceladas se tratan como pendientes visualmente, pero no se puede pagar
  const isPaymentButtonDisabled = (factura) => {
    // Si está pagado, no se puede pagar
    if (factura.estado === "pagado") return true;
    // Si está cancelado, tampoco se puede pagar (solo se muestra como pendiente)
    if (factura.estado === "cancelado") return true;
    if (loading) return true;
    if (processingPaymentId === factura.id) return true;
    if (disabledPayments[factura.id]) return true;
    if (factura.paymentWindowOpened) return true;
    if (factura.paymentUrlUsed) return true;
    return false;
  };

  const getPaymentButtonColor = (factura) => {
    if (factura.estado === "pagado") return "#a0a0a0";
    if (factura.estado === "cancelado") return "#a0a0a0";
    if (disabledPayments[factura.id]) return "#a0a0a0";
    if (factura.paymentWindowOpened || factura.paymentUrlUsed) return "#a0a0a0";
    if (processingPaymentId === factura.id) return colors.warning;
    return colors.primary;
  };

  const getPaymentButtonTooltip = (factura) => {
    if (factura.estado === "pagado") return "Factura ya pagada";
    if (factura.estado === "cancelado")
      return "Factura cancelada (no se puede pagar)";
    if (disabledPayments[factura.id]) return "Pago deshabilitado";
    if (factura.paymentWindowOpened || factura.paymentUrlUsed)
      return "Enlace ya utilizado - Debe generar uno nuevo";
    if (processingPaymentId === factura.id) return "Procesando pago...";
    return "Pagar";
  };

  const handlePagarClick = async (factura) => {
    // Deshabilitar el botón inmediatamente después del clic
    setDisabledPayments((prev) => ({
      ...prev,
      [factura.id]: true,
    }));

    try {
      await onPagar(factura);
    } catch (error) {
      // Si hay error, habilitar el botón nuevamente
      setDisabledPayments((prev) => ({
        ...prev,
        [factura.id]: false,
      }));
    }
  };

  const handleEliminarClick = (facturaId) => {
    // Actualizar el estado local para deshabilitar pagos después de eliminar
    setDisabledPayments((prev) => ({
      ...prev,
      [facturaId]: true,
    }));
    onEliminar(facturaId);
  };

  return (
    <Box sx={{ mt: 3 }}>
      <Card
        sx={{
          mb: 4,
          borderRadius: 2,
          boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
          background: colors.white,
          border: `1px solid ${colors.primary}30`,
        }}
      >
        <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
          <Typography
            variant="h5"
            gutterBottom
            sx={{
              color: colors.primary,
              fontWeight: "bold",
              mb: 1,
              fontSize: { xs: "1.25rem", sm: "1.5rem" },
            }}
          >
            Buscar Facturas
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: colors.textLight, mb: { xs: 2, sm: 3, md: 4 } }}
          >
            Ingrese el NIT y el código empresarial para consultar las facturas
          </Typography>

          <Grid container spacing={2} alignItems="flex-start">
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="NIT / Identificación"
                value={nitBusqueda}
                onChange={(e) => {
                  setNitBusqueda(e.target.value);
                  setErrorBusqueda("");
                }}
                onKeyPress={handleKeyPress}
                error={!!errorBusqueda && !facturasEncontradas.length}
                helperText="Ingrese el NIT (ej: 900123456-7)"
                variant="outlined"
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#cbd5e1" },
                    "&:hover fieldset": { borderColor: colors.primary },
                    "&.Mui-focused fieldset": { borderColor: colors.primary },
                  },
                  "& .MuiInputLabel-root": { color: "#64748b" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.primary,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <BusinessIcon sx={{ mr: 1, color: colors.primary }} />
                  ),
                }}
                placeholder="Ej: 900123456-7"
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Código Empresarial"
                value={codigoEmpresarial}
                onChange={(e) => {
                  setCodigoEmpresarial(e.target.value);
                  setErrorBusqueda("");
                }}
                onKeyPress={handleKeyPress}
                error={!!errorBusqueda && !facturasEncontradas.length}
                helperText="Ingrese el código empresarial"
                variant="outlined"
                type={showCodigo ? "text" : "password"}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    "& fieldset": { borderColor: "#cbd5e1" },
                    "&:hover fieldset": { borderColor: colors.primary },
                    "&.Mui-focused fieldset": { borderColor: colors.primary },
                  },
                  "& .MuiInputLabel-root": { color: "#64748b" },
                  "& .MuiInputLabel-root.Mui-focused": {
                    color: colors.primary,
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <SecurityIcon sx={{ mr: 1, color: colors.primary }} />
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="toggle password visibility"
                        onClick={() => setShowCodigo(!showCodigo)}
                        edge="end"
                        sx={{ color: colors.primary }}
                      >
                        {showCodigo ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                placeholder="Ej: EMP12345"
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleBuscarPorNit}
                disabled={
                  buscando || !nitBusqueda.trim() || !codigoEmpresarial.trim()
                }
                sx={{
                  height: "56px",
                  bgcolor: colors.primary,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  fontWeight: "bold",
                  textTransform: "none",
                  color: colors.secondary,
                  "&:hover": { bgcolor: colors.primaryHover },
                  "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#94a3b8" },
                }}
              >
                {buscando ? (
                  <CircularProgress
                    size={24}
                    sx={{ color: colors.secondary }}
                  />
                ) : (
                  <>
                    <SearchIcon sx={{ mr: 1 }} />
                    Buscar
                  </>
                )}
              </Button>
            </Grid>
          </Grid>

          {errorBusqueda && (
            <Alert
              severity="error"
              sx={{ mt: 2 }}
              onClose={() => setErrorBusqueda("")}
            >
              {errorBusqueda}
            </Alert>
          )}
        </CardContent>
      </Card>

      {facturasEncontradas.length > 0 && (
        <Fade in>
          <Paper
            sx={{
              borderRadius: 2,
              overflow: "hidden",
              boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              border: `1px solid ${colors.primary}30`,
            }}
          >
            <Box
              sx={{
                p: { xs: 1.5, sm: 2, md: 3 },
                bgcolor: colors.background,
                borderBottom: `1px solid ${colors.primary}30`,
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{
                  color: colors.secondary,
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                }}
              >
                Facturas Encontradas
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: colors.textLight,
                  fontSize: { xs: "0.7rem", sm: "0.875rem" },
                }}
              >
                NIT: {nitBusqueda} | Total: {facturasEncontradas.length}{" "}
                facturas
              </Typography>
            </Box>

            <TableContainer sx={{ overflowX: "auto" }}>
              <Table>
                <TableHead sx={{ bgcolor: colors.background }}>
                  <TableRow>
                    <TableCell
                      sx={{ fontWeight: "bold", color: colors.secondary }}
                    >
                      Fecha
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", color: colors.secondary }}
                    >
                      Empresa
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", color: colors.secondary }}
                    >
                      NIT / ID
                    </TableCell>
                    <TableCell
                      sx={{ fontWeight: "bold", color: colors.secondary }}
                    >
                      N° Factura
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={{ fontWeight: "bold", color: colors.secondary }}
                    >
                      Valor
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold", color: colors.secondary }}
                    >
                      Estado
                    </TableCell>
                    <TableCell
                      align="center"
                      sx={{ fontWeight: "bold", color: colors.secondary }}
                    >
                      Acciones
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {facturasEncontradas.map((factura) => {
                    const chipStyles = getChipStyles(factura.estado);
                    const visualState = getVisualState(factura.estado);
                    const isCanceled =
                      factura.estado?.toLowerCase() === "cancelado";

                    return (
                      <TableRow
                        key={factura.id}
                        hover
                        sx={{
                          "&:hover": { bgcolor: colors.background },
                          "& td": {
                            color: colors.secondary,
                            borderBottom: `1px solid ${colors.primary}20`,
                          },
                        }}
                      >
                        <TableCell>{formatDate(factura.fecha)}</TableCell>
                        <TableCell>{factura.nombreEmpresa}</TableCell>
                        <TableCell>{factura.nitEmpresa}</TableCell>
                        <TableCell>{factura.numeroFactura}</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold" color={colors.primary}>
                            $
                            {factura.valorPagar?.toLocaleString("es-CO") ||
                              factura.valorPagar}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={chipStyles.icon}
                            label={chipStyles.label}
                            size="small"
                            sx={{
                              bgcolor: chipStyles.bgcolor,
                              color: chipStyles.color,
                              fontWeight: "bold",
                            }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={getPaymentButtonTooltip(factura)}>
                            <IconButton
                              size="small"
                              onClick={() => handlePagarClick(factura)}
                              disabled={isPaymentButtonDisabled(factura)}
                              sx={{
                                color: getPaymentButtonColor(factura),
                                "&:hover": { bgcolor: "#d4edda" },
                              }}
                            >
                              {processingPaymentId === factura.id ? (
                                <CircularProgress
                                  size={20}
                                  sx={{ color: colors.warning }}
                                />
                              ) : (
                                <PaymentIcon fontSize="medium" />
                              )}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Eliminar">
                            <IconButton
                              size="small"
                              onClick={() => handleEliminarClick(factura.id)}
                              disabled={
                                factura.estado === "pagado" ||
                                factura.estado === "cancelado"
                              }
                              sx={{
                                color: colors.error,
                                "&:hover": { bgcolor: "#f8d7da" },
                                "&.Mui-disabled": { color: "#a0a0a0" },
                              }}
                            >
                              <DeleteIcon fontSize="medium" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Fade>
      )}
    </Box>
  );
};

const GoPassPortal = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [tabValue, setTabValue] = useState(0);

  const [nombreEmpresa, setNombreEmpresa] = useState("");
  const [nitEmpresa, setNitEmpresa] = useState("");
  const [numeroFactura, setNumeroFactura] = useState("");
  const [valorPagar, setValorPagar] = useState("");
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [processingPaymentId, setProcessingPaymentId] = useState(null);
  const [paymentDialog, setPaymentDialog] = useState({
    open: false,
    url: "",
    paymentId: null,
    amount: 0,
    facturaId: null,
  });
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const [formErrors, setFormErrors] = useState({});
  const [csrfToken, setCsrfToken] = useState("");

  // Generar CSRF token al inicio
  useEffect(() => {
    const generateCsrfToken = () => {
      const token = `${Date.now()}-${Math.random().toString(36).substr(2, 16)}-${navigator.userAgent.length}`;
      setCsrfToken(token);
      sessionStorage.setItem("csrf_token", token);
    };
    generateCsrfToken();
    if (paymentService.cleanExpiredPaymentUrls) {
      paymentService.cleanExpiredPaymentUrls();
    }
  }, []);

  // Función para sanitizar
  const sanitizeText = (text) => {
    if (!text) return text;
    return text.replace(/[<>{}[\]|\\]/g, "");
  };

  const sanitizeIdentifier = (text) => {
    if (!text) return text;
    return text.replace(/[^a-zA-Z0-9-]/g, "");
  };

  const sanitizeInvoiceNumber = (text) => {
    if (!text) return text;
    return text.replace(/[^a-zA-Z0-9\s\-.]/g, "");
  };

  // Validaciones
  const validateCompanyName = (name) => {
    if (!name || name.trim().length < 2) {
      return "El nombre debe tener al menos 2 caracteres";
    }
    if (name.length > 150) {
      return "El nombre no puede exceder 150 caracteres";
    }
    if (/[<>{}[\]|\\]/.test(name)) {
      return "El nombre contiene caracteres no permitidos";
    }
    return null;
  };

  const validateIdentifier = (nit) => {
    if (!nit || nit.trim().length < 3) {
      return "La identificación debe tener al menos 3 caracteres";
    }
    if (nit.length > 30) {
      return "La identificación no puede exceder 30 caracteres";
    }
    if (/[<>{}[\]|\\]/.test(nit)) {
      return "La identificación contiene caracteres no permitidos";
    }
    return null;
  };

  const validateInvoiceNumber = (numero) => {
    if (!numero || numero.trim().length < 1) {
      return "El número de factura es obligatorio";
    }
    if (numero.length > 30) {
      return "El número de factura no puede exceder 30 caracteres";
    }
    if (/[<>{}[\]|\\]/.test(numero)) {
      return "El número de factura contiene caracteres no permitidos";
    }
    return null;
  };

  const validateAmount = (amount) => {
    const num = parseFloat(amount);
    if (isNaN(num) || num <= 0) {
      return "El monto debe ser mayor a 0";
    }
    if (num > 100000000) {
      return "El monto excede el límite máximo permitido";
    }
    if (!/^\d+(\.\d{1,2})?$/.test(amount)) {
      return "El monto debe tener máximo 2 decimales";
    }
    return null;
  };

  const validateCsrfToken = (token) => {
    const storedToken = sessionStorage.getItem("csrf_token");
    return token === storedToken;
  };

  const handleNombreChange = (e) => {
    const value = sanitizeText(e.target.value);
    setNombreEmpresa(value);
    setFormErrors({ ...formErrors, nombreEmpresa: null });
  };

  const handleNitChange = (e) => {
    const value = sanitizeIdentifier(e.target.value);
    setNitEmpresa(value);
    setFormErrors({ ...formErrors, nitEmpresa: null });
  };

  const handleNumeroFacturaChange = (e) => {
    const value = sanitizeInvoiceNumber(e.target.value);
    setNumeroFactura(value);
    setFormErrors({ ...formErrors, numeroFactura: null });
  };

  const handleValorChange = (e) => {
    const value = e.target.value;
    if (value === "" || /^\d*\.?\d{0,2}$/.test(value)) {
      setValorPagar(value);
      setFormErrors({ ...formErrors, valorPagar: null });
    }
  };

  const registrarFactura = () => {
    if (!validateCsrfToken(csrfToken)) {
      setSnackbar({
        open: true,
        message: "Error de seguridad. Recargue la página.",
        severity: "error",
      });
      return;
    }

    const errors = {};

    const nameError = validateCompanyName(nombreEmpresa);
    if (nameError) errors.nombreEmpresa = nameError;

    const nitError = validateIdentifier(nitEmpresa);
    if (nitError) errors.nitEmpresa = nitError;

    const facturaError = validateInvoiceNumber(numeroFactura);
    if (facturaError) errors.numeroFactura = facturaError;

    const amountError = validateAmount(valorPagar);
    if (amountError) errors.valorPagar = amountError;

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      setSnackbar({
        open: true,
        message: "Por favor corrija los errores en el formulario",
        severity: "warning",
      });
      return;
    }

    setFormErrors({});

    const nuevaFactura = {
      id: Date.now(),
      nombreEmpresa: sanitizeText(nombreEmpresa),
      nitEmpresa: sanitizeIdentifier(nitEmpresa),
      numeroFactura: sanitizeInvoiceNumber(numeroFactura),
      valorPagar: parseFloat(valorPagar),
      fecha: new Date().toLocaleString("es-CO"),
      estado: "pendiente",
      paymentId: null,
      paymentUrl: null,
      paymentUrlUsed: false,
      paymentWindowOpened: false,
      createdAt: Date.now(),
    };

    setFacturas([nuevaFactura, ...facturas]);
    setNombreEmpresa("");
    setNitEmpresa("");
    setNumeroFactura("");
    setValorPagar("");

    setSnackbar({
      open: true,
      message: "Factura registrada exitosamente",
      severity: "success",
    });
  };

  const handlePagar = async (factura) => {
    if (!validateCsrfToken(csrfToken)) {
      setSnackbar({
        open: true,
        message: "Error de seguridad. Recargue la página.",
        severity: "error",
      });
      return;
    }

    if (factura.estado === "pagado") {
      setSnackbar({
        open: true,
        message: "Esta factura ya fue pagada",
        severity: "warning",
      });
      return;
    }

    if (factura.estado === "cancelado") {
      setSnackbar({
        open: true,
        message: "Esta factura fue cancelada",
        severity: "warning",
      });
      return;
    }

    if (
      factura.paymentUrl &&
      (factura.paymentUrlUsed || factura.paymentWindowOpened)
    ) {
      setSnackbar({
        open: true,
        message: "El enlace de pago ya fue utilizado. Debe generar uno nuevo.",
        severity: "warning",
      });
      return;
    }

    setProcessingPaymentId(factura.id);
    setLoading(true);

    try {
      const response = await paymentService.createPayment(factura.valorPagar, {
        companyName: factura.nombreEmpresa,
        nit: factura.nitEmpresa,
        facturaNumero: factura.numeroFactura,
      });

      const paymentUrl = response.data?.url;
      const paymentId = response.data?.id;

      if (!paymentUrl) {
        throw new Error("No se recibió URL de pago");
      }

      if (
        paymentService.validatePaymentUrl &&
        !paymentService.validatePaymentUrl(paymentUrl)
      ) {
        throw new Error("URL de pago no válida");
      }

      if (paymentService.storePaymentUrlSecurely) {
        paymentService.storePaymentUrlSecurely(paymentId, paymentUrl);
      }

      const pagoData = {
        monto: factura.valorPagar,
        facturaNumero: factura.numeroFactura,
        empresa: factura.nombreEmpresa,
        nit: factura.nitEmpresa,
        fecha: new Date().toLocaleString("es-CO"),
        transactionId: paymentId,
        paymentUrl: paymentUrl,
      };

      localStorage.setItem(
        `payment_data_${paymentId}`,
        JSON.stringify(pagoData),
      );

      setFacturas((prevFacturas) =>
        prevFacturas.map((f) =>
          f.id === factura.id
            ? {
                ...f,
                paymentId: paymentId,
                paymentUrl: paymentService.getSecurePaymentUrl
                  ? paymentService.getSecurePaymentUrl(paymentId)
                  : paymentUrl,
                paymentWindowOpened: true,
              }
            : f,
        ),
      );

      window.open(paymentUrl, "_blank", "noopener,noreferrer");

      setPaymentDialog({
        open: true,
        url: paymentUrl,
        paymentId: paymentId,
        amount: factura.valorPagar,
        facturaId: factura.id,
      });

      setSnackbar({
        open: true,
        message: "Página de pago abierta en nueva pestaña.",
        severity: "info",
      });
    } catch (error) {
      console.error("Error al procesar pago:", error);
      setSnackbar({
        open: true,
        message: error.message || "Error al procesar el pago",
        severity: "error",
      });
      setProcessingPaymentId(null);
    } finally {
      setLoading(false);
    }
  };

  const handleClosePaymentDialog = () => {
    if (paymentDialog.facturaId) {
      setFacturas((prevFacturas) =>
        prevFacturas.map((f) =>
          f.id === paymentDialog.facturaId ? { ...f, paymentUrlUsed: true } : f,
        ),
      );
    }

    setPaymentDialog({
      open: false,
      url: "",
      paymentId: null,
      amount: 0,
      facturaId: null,
    });
    setProcessingPaymentId(null);
  };

  const handleEliminar = (facturaId) => {
    // Primero buscar en las facturas registradas localmente
    const facturaLocal = facturas.find((f) => f.id === facturaId);

    // Si existe en facturas locales, verificar su estado
    if (facturaLocal) {
      if (facturaLocal.estado === "pagado") {
        setSnackbar({
          open: true,
          message: "No se puede eliminar una factura ya pagada",
          severity: "warning",
        });
        return;
      }

      if (facturaLocal.estado === "cancelado") {
        setSnackbar({
          open: true,
          message: "No se puede eliminar una factura cancelada",
          severity: "warning",
        });
        return;
      }

      // Eliminar de facturas locales
      setFacturas(facturas.filter((f) => f.id !== facturaId));
      setSnackbar({
        open: true,
        message: "Factura eliminada exitosamente",
        severity: "success",
      });
    } else {
      // Si no está en facturas locales, significa que viene de la búsqueda
      // Mostrar mensaje indicando que solo se pueden eliminar facturas registradas localmente
      setSnackbar({
        open: true,
        message: "Solo se pueden eliminar facturas registradas en este portal",
        severity: "warning",
      });
    }
  };

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const getChipStyles = (estado) => {
    switch (estado) {
      case "pagado":
        return {
          bgcolor: "#d4edda",
          color: "#155724",
          icon: <CheckCircleIcon />,
          label: "Pagado",
        };
      case "cancelado":
        return {
          bgcolor: "#f8d7da",
          color: "#721c24",
          icon: <CancelIcon />,
          label: "Cancelado",
        };
      default:
        return {
          bgcolor: "#fff3cd",
          color: "#856404",
          icon: <ReceiptIcon />,
          label: "Pendiente",
        };
    }
  };

  const isPaymentButtonDisabled = (factura) => {
    if (factura.estado === "pagado") return true;
    if (factura.estado === "cancelado") return true;
    if (loading) return true;
    if (processingPaymentId === factura.id) return true;
    if (factura.paymentWindowOpened) return true;
    if (factura.paymentUrlUsed) return true;
    return false;
  };

  const getPaymentButtonColor = (factura) => {
    if (factura.estado === "pagado") return "#a0a0a0";
    if (factura.estado === "cancelado") return "#a0a0a0";
    if (factura.paymentWindowOpened || factura.paymentUrlUsed) return "#a0a0a0";
    if (processingPaymentId === factura.id) return colors.warning;
    return colors.primary;
  };

  const getPaymentButtonTooltip = (factura) => {
    if (factura.estado === "pagado") return "Factura ya pagada";
    if (factura.estado === "cancelado") return "Factura cancelada";
    if (factura.paymentWindowOpened || factura.paymentUrlUsed)
      return "Enlace ya utilizado - Debe generar uno nuevo";
    if (processingPaymentId === factura.id) return "Procesando pago...";
    return "Pagar";
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: colors.white,
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 2, sm: 3, md: 4 } }}>
        {/* Banner de seguridad */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 3 },
            background: colors.secondary,
            color: colors.white,
            borderRadius: 2,
            border: `1px solid ${colors.primary}`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: 1,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <SecurityIcon
              sx={{ color: colors.primary, fontSize: { xs: 16, sm: 20 } }}
            />
            <Typography
              variant="body2"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
            >
              Conexión Segura
            </Typography>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <VerifiedUserIcon
              sx={{ color: colors.primary, fontSize: { xs: 16, sm: 20 } }}
            />
            <Typography
              variant="body2"
              sx={{ fontSize: { xs: "0.7rem", sm: "0.875rem" } }}
            >
              Validación Activada
            </Typography>
          </Box>
        </Paper>

        {/* Banner principal con logo */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, sm: 4, md: 5 },
            mb: { xs: 3, sm: 4, md: 5 },
            background: colors.white,
            color: colors.secondary,
            borderRadius: 2,
            border: `1px solid ${colors.primary}`,
            textAlign: "center",
            boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
          }}
        >
          <Box
            component="img"
            src="/img/logohorinzontal.png"
            alt="GoPass"
            sx={{
              height: { xs: 55, sm: 75, md: 85 },
              width: "auto",
              objectFit: "contain",
              mb: 2,
            }}
            onError={(e) => {
              e.target.style.display = "none";
            }}
          />
          <Typography
            variant={isMobile ? "h4" : "h3"}
            gutterBottom
            sx={{
              fontWeight: "bold",
              fontSize: { xs: "1.6rem", sm: "2rem", md: "2.5rem" },
              color: colors.secondary,
              letterSpacing: "-0.5px",
            }}
          >
            Portal de Recaudos
          </Typography>
          <Typography
            variant="subtitle1"
            sx={{
              color: colors.textLight,
              fontSize: { xs: "0.85rem", sm: "1rem", md: "1.1rem" },
              maxWidth: "600px",
              margin: "0 auto",
            }}
          >
            Gestión segura de facturas y pagos empresariales
          </Typography>
        </Paper>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs
            value={tabValue}
            onChange={handleTabChange}
            sx={{
              "& .MuiTab-root": {
                fontSize: { xs: "0.875rem", sm: "1rem" },
                textTransform: "none",
                fontWeight: "bold",
                minWidth: { xs: "auto", sm: 120 },
                "&.Mui-selected": {
                  color: colors.primary,
                },
              },
              "& .MuiTabs-indicator": {
                backgroundColor: colors.primary,
              },
            }}
          >
            <Tab label="Registrar Factura" />
            <Tab label="Buscar Facturas" />
          </Tabs>
        </Box>

        {/* Contenido de los tabs */}
        {tabValue === 0 && (
          <>
            {/* Formulario */}
            <Card
              sx={{
                mb: { xs: 2, sm: 3, md: 4 },
                borderRadius: 2,
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                background: colors.white,
                border: `1px solid ${colors.primary}30`,
              }}
            >
              <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 } }}>
                <Typography
                  variant="h5"
                  gutterBottom
                  sx={{
                    color: colors.primary,
                    fontWeight: "bold",
                    mb: 1,
                    fontSize: { xs: "1.25rem", sm: "1.5rem" },
                  }}
                >
                  Registrar Nueva Factura
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: colors.textLight, mb: { xs: 2, sm: 3, md: 4 } }}
                >
                  Complete los datos (todos los campos son obligatorios)
                </Typography>

                <Grid container spacing={{ xs: 2, sm: 3 }}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Nombre del Usuario o Empresa"
                      value={nombreEmpresa}
                      onChange={handleNombreChange}
                      error={!!formErrors.nombreEmpresa}
                      helperText={
                        formErrors.nombreEmpresa ||
                        "Ingrese el nombre del Usuario o empresa (mínimo 2 caracteres)"
                      }
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#cbd5e1" },
                          "&:hover fieldset": { borderColor: colors.primary },
                          "&.Mui-focused fieldset": {
                            borderColor: colors.primary,
                          },
                        },
                        "& .MuiInputLabel-root": { color: "#64748b" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: colors.primary,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <BusinessIcon sx={{ mr: 1, color: colors.primary }} />
                        ),
                      }}
                      placeholder="Ej: Empresa ABC SAS o Nombre usuario"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="NIT / Identificación"
                      value={nitEmpresa}
                      onChange={handleNitChange}
                      error={!!formErrors.nitEmpresa}
                      helperText={
                        formErrors.nitEmpresa ||
                        "Ingrese NIT o número de identificación (ej: 900123456-7, 12345678)"
                      }
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#cbd5e1" },
                          "&:hover fieldset": { borderColor: colors.primary },
                          "&.Mui-focused fieldset": {
                            borderColor: colors.primary,
                          },
                        },
                        "& .MuiInputLabel-root": { color: "#64748b" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: colors.primary,
                        },
                      }}
                      placeholder="Ej: 900123456-7"
                    />
                  </Grid>

                  <Grid item xs={12} md={6}>
                    <TextField
                      fullWidth
                      label="Número de Factura"
                      value={numeroFactura}
                      onChange={handleNumeroFacturaChange}
                      error={!!formErrors.numeroFactura}
                      helperText={
                        formErrors.numeroFactura ||
                        "Ingrese el número de factura (ej: FAC-001, 001, INV-2024-001)"
                      }
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#cbd5e1" },
                          "&:hover fieldset": { borderColor: colors.primary },
                          "&.Mui-focused fieldset": {
                            borderColor: colors.primary,
                          },
                        },
                        "& .MuiInputLabel-root": { color: "#64748b" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: colors.primary,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <ReceiptLongIcon
                            sx={{ mr: 1, color: colors.primary }}
                          />
                        ),
                      }}
                      placeholder="Ej: FAC-001"
                    />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      type="text"
                      label="Valor a Pagar (COP)"
                      value={valorPagar}
                      onChange={handleValorChange}
                      error={!!formErrors.valorPagar}
                      helperText={
                        formErrors.valorPagar ||
                        "Monto en pesos colombianos (máximo 2 decimales)"
                      }
                      variant="outlined"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          "& fieldset": { borderColor: "#cbd5e1" },
                          "&:hover fieldset": { borderColor: colors.primary },
                          "&.Mui-focused fieldset": {
                            borderColor: colors.primary,
                          },
                        },
                        "& .MuiInputLabel-root": { color: "#64748b" },
                        "& .MuiInputLabel-root.Mui-focused": {
                          color: colors.primary,
                        },
                      }}
                      InputProps={{
                        startAdornment: (
                          <MoneyIcon sx={{ mr: 1, color: colors.primary }} />
                        ),
                        endAdornment: (
                          <Typography sx={{ color: "#64748b" }}>COP</Typography>
                        ),
                      }}
                      placeholder="0"
                    />
                  </Grid>
                </Grid>

                <Button
                  fullWidth
                  variant="contained"
                  onClick={registrarFactura}
                  disabled={
                    !nombreEmpresa ||
                    !nitEmpresa ||
                    !numeroFactura ||
                    !valorPagar
                  }
                  sx={{
                    mt: { xs: 3, sm: 4 },
                    bgcolor: colors.primary,
                    py: { xs: 1.2, sm: 1.5 },
                    fontSize: { xs: "0.9rem", sm: "1rem" },
                    fontWeight: "bold",
                    textTransform: "none",
                    color: colors.secondary,
                    "&:hover": { bgcolor: colors.primaryHover },
                    "&.Mui-disabled": { bgcolor: "#cbd5e1", color: "#94a3b8" },
                  }}
                >
                  Registrar Factura
                </Button>
              </CardContent>
            </Card>

            {/* Tabla de facturas registradas */}
            {facturas.length > 0 && (
              <Fade in>
                <Paper
                  sx={{
                    borderRadius: 2,
                    overflow: "hidden",
                    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
                    border: `1px solid ${colors.primary}30`,
                  }}
                >
                  <Box
                    sx={{
                      p: { xs: 1.5, sm: 2, md: 3 },
                      bgcolor: colors.background,
                      borderBottom: `1px solid ${colors.primary}30`,
                    }}
                  >
                    <Typography
                      variant="h6"
                      fontWeight="bold"
                      sx={{
                        color: colors.secondary,
                        fontSize: { xs: "1rem", sm: "1.1rem" },
                      }}
                    >
                      Facturas Registradas
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: colors.textLight,
                        fontSize: { xs: "0.7rem", sm: "0.875rem" },
                      }}
                    >
                      Total: {facturas.length} facturas
                    </Typography>
                  </Box>

                  <TableContainer sx={{ overflowX: "auto" }}>
                    <Table>
                      <TableHead sx={{ bgcolor: colors.background }}>
                        <TableRow>
                          <TableCell
                            sx={{ fontWeight: "bold", color: colors.secondary }}
                          >
                            Fecha
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", color: colors.secondary }}
                          >
                            Empresa
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", color: colors.secondary }}
                          >
                            NIT / ID
                          </TableCell>
                          <TableCell
                            sx={{ fontWeight: "bold", color: colors.secondary }}
                          >
                            N° Factura
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ fontWeight: "bold", color: colors.secondary }}
                          >
                            Valor
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: "bold", color: colors.secondary }}
                          >
                            Estado
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ fontWeight: "bold", color: colors.secondary }}
                          >
                            Acciones
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {facturas.map((factura) => {
                          const chipStyles = getChipStyles(factura.estado);
                          return (
                            <TableRow
                              key={factura.id}
                              hover
                              sx={{
                                "&:hover": { bgcolor: colors.background },
                                "& td": {
                                  color: colors.secondary,
                                  borderBottom: `1px solid ${colors.primary}20`,
                                },
                              }}
                            >
                              <TableCell>{factura.fecha}</TableCell>
                              <TableCell>{factura.nombreEmpresa}</TableCell>
                              <TableCell>{factura.nitEmpresa}</TableCell>
                              <TableCell>{factura.numeroFactura}</TableCell>
                              <TableCell align="right">
                                <Typography
                                  fontWeight="bold"
                                  color={colors.primary}
                                >
                                  ${factura.valorPagar.toLocaleString("es-CO")}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                <Chip
                                  icon={chipStyles.icon}
                                  label={chipStyles.label}
                                  size="small"
                                  sx={{
                                    bgcolor: chipStyles.bgcolor,
                                    color: chipStyles.color,
                                    fontWeight: "bold",
                                  }}
                                />
                              </TableCell>
                              <TableCell align="center">
                                <Tooltip
                                  title={getPaymentButtonTooltip(factura)}
                                >
                                  <IconButton
                                    size="small"
                                    onClick={() => handlePagar(factura)}
                                    disabled={isPaymentButtonDisabled(factura)}
                                    sx={{
                                      color: getPaymentButtonColor(factura),
                                      "&:hover": { bgcolor: "#d4edda" },
                                    }}
                                  >
                                    {processingPaymentId === factura.id ? (
                                      <CircularProgress
                                        size={20}
                                        sx={{ color: colors.warning }}
                                      />
                                    ) : (
                                      <PaymentIcon
                                        fontSize={isMobile ? "small" : "medium"}
                                      />
                                    )}
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Eliminar">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEliminar(factura.id)}
                                    disabled={
                                      factura.estado === "pagado" ||
                                      factura.estado === "cancelado"
                                    }
                                    sx={{
                                      color: colors.error,
                                      "&:hover": { bgcolor: "#f8d7da" },
                                      "&.Mui-disabled": { color: "#a0a0a0" },
                                    }}
                                  >
                                    <DeleteIcon
                                      fontSize={isMobile ? "small" : "medium"}
                                    />
                                  </IconButton>
                                </Tooltip>
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              </Fade>
            )}

            {facturas.length === 0 && (
              <Paper
                sx={{
                  p: { xs: 3, sm: 4 },
                  textAlign: "center",
                  bgcolor: colors.white,
                  border: `1px solid ${colors.primary}30`,
                  borderRadius: 2,
                  boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                }}
              >
                <ReceiptIcon
                  sx={{ fontSize: { xs: 40, sm: 60 }, color: "#cbd5e1", mb: 2 }}
                />
                <Typography sx={{ color: colors.textLight }}>
                  No hay facturas registradas. Complete el formulario para
                  comenzar.
                </Typography>
              </Paper>
            )}
          </>
        )}

        {tabValue === 1 && (
          <BuscarFacturasTab
            colors={colors}
            onPagar={handlePagar}
            onEliminar={handleEliminar}
            processingPaymentId={processingPaymentId}
            loading={loading}
          />
        )}
      </Container>

      {/* Diálogo de pago */}
      <Dialog
        open={paymentDialog.open}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
          },
        }}
      >
        <DialogTitle sx={{ color: colors.primary, fontWeight: "bold" }}>
          Pago en Proceso
        </DialogTitle>
        <DialogContent sx={{ mt: 2 }}>
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              La página de pago se ha abierto en una nueva pestaña.
            </Alert>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Este enlace es de un solo uso. Al cerrar este diálogo, el botón de
              pago quedará deshabilitado permanentemente para esta factura.
            </Alert>
            <Typography
              variant="body2"
              sx={{
                color: colors.textLight,
                mt: 2,
                display: "block",
                textAlign: "center",
              }}
            >
              Monto a pagar:{" "}
              <strong style={{ color: colors.primary }}>
                ${paymentDialog.amount.toLocaleString("es-CO")}
              </strong>
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions sx={{ borderTop: "1px solid #e2e8f0", p: 2 }}>
          <Button
            onClick={handleClosePaymentDialog}
            variant="contained"
            sx={{
              bgcolor: colors.primary,
              color: colors.secondary,
              "&:hover": { bgcolor: colors.primaryHover },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {loading && !paymentDialog.open && (
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            bgcolor: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 2000,
          }}
        >
          <CircularProgress sx={{ color: colors.primary }} />
          <Typography
            sx={{
              color: colors.white,
              ml: 2,
              fontSize: { xs: "0.8rem", sm: "1rem" },
            }}
          >
            Generando pago...
          </Typography>
        </Box>
      )}

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          severity={snackbar.severity}
          sx={{ fontSize: { xs: "0.75rem", sm: "0.875rem" } }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GoPassPortal;
